import { useCallback, useMemo } from 'react';
import { createFileRoute, Outlet, useRouter } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { EntityIndexLayout, WorkspaceShell, useWorkspaceHeader } from '@/components/layouts';
import { CrewListPanel } from '@/components/organisms/crews/CrewListPanel';
import { CrewTableView } from '@/components/organisms/crews/CrewTableView';
import { useEntityRouteState } from '@/hooks/navigation/useEntityRouteState';
import { useHasCapability } from '@/hooks/usePermissions';
import {
  CREW_MEMBER_FILTERS,
  CREW_SORT_OPTIONS,
  DEFAULT_CREW_LIST_FILTERS,
  resolveCrewListFilters,
  type CrewListFilters,
  type CrewMemberFilter,
} from '@/components/organisms/crews/crew-list-utils';

type CrewRouteSearch = Partial<CrewListFilters>;

export const Route = createFileRoute('/_dashboard/dashboard/crews')({
  validateSearch: (search: Record<string, unknown>): CrewRouteSearch => {
    const sort = Number(search.sort);

    return {
      q: typeof search.q === 'string' ? search.q : DEFAULT_CREW_LIST_FILTERS.q,
      memberStatus: CREW_MEMBER_FILTERS.some((filter) => filter.value === search.memberStatus)
        ? (search.memberStatus as CrewMemberFilter)
        : DEFAULT_CREW_LIST_FILTERS.memberStatus,
      timezone: typeof search.timezone === 'string' ? search.timezone : '',
      sort: Number.isFinite(sort)
        ? Math.min(Math.max(Math.floor(sort), 0), CREW_SORT_OPTIONS.length - 1)
        : DEFAULT_CREW_LIST_FILTERS.sort,
    };
  },
  component: CrewsLayout,
});

function cleanSearch(next: Partial<CrewListFilters>) {
  const clean: Partial<CrewListFilters> = { ...next };
  const defaults: Partial<Record<keyof CrewListFilters, unknown>> = {
    q: '',
    memberStatus: 'all',
    timezone: '',
    sort: 0,
  };

  (Object.keys(clean) as Array<keyof CrewListFilters>).forEach((key) => {
    if (clean[key] == null || clean[key] === defaults[key]) delete clean[key];
  });

  return clean as CrewRouteSearch;
}

function CrewsLayout() {
  const router = useRouter();
  const routeSearch = Route.useSearch();
  const filters = useMemo(() => resolveCrewListFilters(routeSearch), [routeSearch]);
  const {
    selectedId,
    isFormMode,
    hasChildRoute,
    isIndexRoute,
    preservedSearch: listSearch,
  } = useEntityRouteState({
    basePath: '/dashboard/crews',
    search: filters,
    cleanSearch,
  });

  const handleSelect = useCallback(
    (id: string) => {
      void router.navigate({
        to: '/dashboard/crews/$id',
        params: { id },
        search: (previous) => previous as never,
      });
    },
    [router],
  );

  const handleFiltersChange = useCallback(
    (patch: Partial<CrewListFilters>) => {
      void router.navigate({
        to: '/dashboard/crews',
        search: cleanSearch({ ...filters, ...patch }),
        replace: true,
      });
    },
    [filters, router],
  );

  const handleNewClick = useCallback(() => {
    void router.navigate({ to: '/dashboard/crews/new', search: listSearch });
  }, [router, listSearch]);

  // Create/edit forms render full-page (own FormPageLayout) — never inside the
  // workspace shell (otherwise its slotless header shows as an empty bar). The
  // detail ($id) stays inside the shell to fill the global contextHeader.
  if (isFormMode) {
    return <Outlet />;
  }

  return (
    <WorkspaceShell>
      <CrewsNewButtonSlot hidden={isFormMode} onClick={handleNewClick} />
      <EntityIndexLayout
        title="Crews"
        storageKey="crews-view"
        hasChildRoute={hasChildRoute}
        onResetToIndex={() => void router.navigate({ to: '/dashboard/crews', search: listSearch })}
        splitWidthClass="w-[360px]"
        tableSheetWidthClass="w-[680px] sm:max-w-[680px]"
        viewToggleShowLabels
        viewToggleActiveTone="muted"
        hideListInSplit={isFormMode}
        splitList={() => (
          <div className="flex h-full flex-col">
            <CrewListPanel
              selectedId={selectedId}
              onSelect={handleSelect}
              filters={filters}
              onFiltersChange={handleFiltersChange}
              autoSelectFirst={isIndexRoute}
            />
          </div>
        )}
        splitDetail={<Outlet />}
        tableView={({ openInSheet }) => (
          <CrewTableView
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onOpen={(id) =>
              openInSheet(
                () =>
                  void router.navigate({
                    to: '/dashboard/crews/$id',
                    params: { id },
                    search: listSearch,
                  }),
              )
            }
          />
        )}
        sheetContent={<Outlet />}
        mobileContent={
          <div className="flex min-h-0 flex-1 flex-col md:hidden">
            {hasChildRoute ? (
              <Outlet />
            ) : (
              <CrewListPanel
                selectedId={selectedId}
                onSelect={handleSelect}
                filters={filters}
                onFiltersChange={handleFiltersChange}
              />
            )}
          </div>
        }
      />
    </WorkspaceShell>
  );
}

function CrewsNewButtonSlot({ hidden, onClick }: { hidden: boolean; onClick: () => void }) {
  const canManageCrew = useHasCapability('can_manage_crew');
  const newButton = useMemo(() => {
    if (hidden || !canManageCrew) return null;
    return (
      <Button size="sm" onClick={onClick}>
        <Plus size={14} />
        <span>
          New<span className="hidden sm:inline"> crew</span>
        </span>
      </Button>
    );
  }, [hidden, canManageCrew, onClick]);

  useWorkspaceHeader({ newButton }, [newButton]);
  return null;
}
